import { useEffect, useState } from "react";

export default function ConfirmModal({
  open,
  title = "Confirm Action",
  message = "Are you sure?",
  confirmText = "Confirm",
  cancelText = "Cancel",

  confirmVariant = "danger", // "danger" | "success" | "warning" | "primary"

  onConfirm = async () => {},
  onCancel = () => {},

  closeOnConfirm = true,
}) {
  const [visible, setVisible] = useState(open);
  const [loading, setLoading] = useState(false);

  // sync open → visible
  useEffect(() => {
    if (open) {
      setVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";

      const t = setTimeout(() => setVisible(false), 180);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ESC close
  useEffect(() => {
    if (!open) return;

    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading) onCancel();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, loading, onCancel]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  if (!visible) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);

      const res = await onConfirm();

      // only close if success or allowed
      if (res !== false && closeOnConfirm) {
        onCancel(); // reuse cancel to close
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const variantStyles = {
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-emerald-600 hover:bg-emerald-700",
    warning: "bg-amber-500 hover:bg-amber-600",
    primary: "bg-blue-600 hover:bg-blue-700",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        onClick={!loading ? onCancel : undefined}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-md mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6 transform transition-all duration-200 ${
          open ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </h2>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {message}
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
          >
            {cancelText}
          </button>

          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white text-sm transition disabled:opacity-60 ${
              variantStyles[confirmVariant]
            }`}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
